import React, { createContext, useContext, useState, useEffect } from "react";
import { addonInvoicePreview, addonSellerAccount, addWalletMoney, createPlan, generatePointsTopupLink, planSwitch, previewPlanSummary, reGeneratePaymentUrl, upgradePlan, paymentMethodChange } from "../services/billing";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { is } from "date-fns/locale";
import { bill } from "../../images";

const RazorpayContext = createContext();

export const RazorpayProvider = ({ children }) => {
  const navigate = useNavigate();
  const redirectionUrl = `${window.location.origin}/billing-section`
  const [loading, setLoading] = useState(false);
  const [razorpayModalData, setRazorpayModalData] = useState({
    shortUrl: null,
    invoiceId: null,
    isLoading: false,
    isRazorPayModal: false,
    isPayment: null,
    isBlockedRedirection: false,
    refetchDataFn: null,
    isPaymentExpired: false,
    isUpgradeCancel: false
  })

  const [invoiceModalData, setInvoiceModalData] = useState({
    isInvoiceDetailsModal: false,
    invoiceDetails: null,
    isLoading: false
  })

  const [addonInvoicePreviewData, setAddonPreviewData] = useState({
    isOpenModal: false,
    previewData: null,
    isLoading: false
  })

  const [isRazorpayModalTimerLoader, setIsRazorpayModalTimerLoader] = useState(false)
  const [isPaymentStatus, setPaymentStatus] = useState(null)
  const [packageId, setPackageId] = useState(null)
  const existingRazorpayProcessingData = JSON.parse(localStorage.getItem('razorpayModalData') || "null");
  const isLogedIn = localStorage.getItem('token');

  useEffect(() => {
    if (existingRazorpayProcessingData) {
      setRazorpayModalData((prev) => ({
        ...prev,
        shortUrl: existingRazorpayProcessingData?.shortUrl,
        invoiceId: existingRazorpayProcessingData?.invoiceId,
        isLoading: existingRazorpayProcessingData?.isLoading,
        isRazorPayModal: existingRazorpayProcessingData?.isRazorPayModal,
        isPaymentExpired: existingRazorpayProcessingData?.isPaymentExpired,
        isBlockedRedirection: true
      }));
    }
  }, []);

  const openPaymentTab = (shortUrl, invoiceId, refetchBilling, isPaymentExpired = false, isUpgradeCancel = false) => {
    const newTab = window.open(shortUrl, "_blank");
    if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
      setRazorpayModalData(prev => ({ ...prev, isBlockedRedirection: true }));
    }
    setRazorpayModalData(prev => ({
      ...prev,
      shortUrl,
      invoiceId,
      isLoading: true,
      isRazorPayModal: true,
      refetchDataFn: refetchBilling,
      isPaymentExpired: isPaymentExpired,
      isUpgradeCancel: isUpgradeCancel
    }));
    setIsRazorpayModalTimerLoader(true);
  };

  const handleAddFunds = async (e, amount) => {
    if (!amount || loading) return;

    setLoading(true);

    try {
      const res = await addWalletMoney({
        amount: Number(amount) * 100,
        callback_url: redirectionUrl
      });

      const apiRes = res?.data?.data;
      const topupLink =
        res?.data?.data?.topup_link || res?.data?.topup_link;

      if (topupLink) {
        toast.success("Redirecting to payment page...");

        // Replace current history (no back to old payment link)
        window.location.replace(topupLink);
      } else {
        toast.error(
          apiRes?.message ||
          "Payment link not generated. Please contact support.",
          { position: "bottom-right" }
        );
      }
    } catch (err) {
      console.error("Add money error", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTopupPoints = async (e, topupAmount) => {
    if (!topupAmount || loading) return;

    setLoading(true);

    try {
      const res = await generatePointsTopupLink({
        amount: Number(topupAmount) * 100,
        callback_url: redirectionUrl
      });

      const topupLink =
        res?.data?.data?.topup_link || res?.data?.topup_link;

      if (topupLink) {
        toast.success("Redirecting to payment page...");
        window.location.replace(topupLink);
      } else {
        toast.error("Unable to generate top-up link.");
      }
    } catch (err) {
      console.error("Topup error", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async (e, setIsLoading, packageType, currentPlan, refetchBilling, paymentMethod, newPaymentFlowState) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let apiRes;
      if (packageType === 'upgrade') {
        let params = {
          package_id: packageId,
          interval: newPaymentFlowState?.interval || 'monthly',
        }
        if (currentPlan?.trial) {
          params = {
            ...params,
            ...(newPaymentFlowState?.billingMode !== 'manual' && {
              payment_method: paymentMethod || 'upi',
            }),
            billing_mode: newPaymentFlowState?.billingMode,
          };
        }
        apiRes = await planSwitch(params);
        const isPaid = apiRes?.data?.data?.paid_plan;
        const shortUrl = apiRes?.data?.data?.payment_link;
        if (shortUrl) {
          const isAutoPay = ((!currentPlan?.is_free)) ? currentPlan?.subscription?.billing_mode === "autopay" : newPaymentFlowState?.billingMode === "autopay";
          if (isAutoPay) {
            openPaymentTab(shortUrl, null, null, true,true);
            return;
          }
          toast.success("Redirecting to payment page...");
          window.location.href = shortUrl;
        }
        else {
          toast.success(
            apiRes?.data?.message ||
            'Your upgrade plan request received successfully. Waiting for payment confirmation.',
            { position: "bottom-right" }
          );
          window.dispatchEvent(new Event("billingUpdated"));
        }
      }
      else if (packageType === 'generatePaymentUrl') {
        apiRes = await paymentMethodChange({
          payment_method: paymentMethod?.toLowerCase(),
          billing_mode: newPaymentFlowState?.billingMode.toLowerCase(),
          callback_url: redirectionUrl
        });
        let shortUrl, invoiceId;
        shortUrl = apiRes?.data?.data?.payment_link;
        if (!shortUrl && newPaymentFlowState?.billingMode.toLowerCase() === "autopay") {
          toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
          return;
        }
        // Open payment tab if shortUrl exists
        const isAutoPay = (newPaymentFlowState?.billingMode.toLowerCase() === "autopay");
        if (isAutoPay) {
          openPaymentTab(shortUrl, invoiceId, null, true,true);
          return;
        }
        toast.success(apiRes?.data?.message || "Billing mode updated successfully.", { position: "bottom-right" });
        window.dispatchEvent(new Event("billingUpdated")); // ← ADD THIS
      }
      else { // pendingPayment or new plan
        let shortUrl, invoiceId;

        if (packageType === 'pendingPayment' && currentPlan) {
          shortUrl = currentPlan?.subscription?.payment_link;
          invoiceId = currentPlan?.subscription?.invoice_id;

          if (!shortUrl) {
            toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
            return;
          }
        } else {
          let payload = {
            plan_id: packageId
          };
          if (!newPaymentFlowState?.isFreePlan) {
            payload = {
              ...payload,
              payment_method: paymentMethod?.toLowerCase(),
              billing_mode: newPaymentFlowState?.billingMode?.toLowerCase(),
              coupon_code: newPaymentFlowState?.coupon_code,
              interval: newPaymentFlowState?.interval
            };
          }
          apiRes = await createPlan(payload);

          if (apiRes?.data?.data?.free_plan) {
            window.location.reload();
          }
          else {
            shortUrl = apiRes?.data?.data?.payment_link;
            invoiceId = apiRes?.data?.data?.invoice?.id;

            if (!shortUrl) {
              toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
              return;
            }
          }
        }
        if (!shortUrl) return;
        const isAutoPay = ((packageType === 'pendingPayment') && (!currentPlan?.is_free)) ? currentPlan?.subscription?.billing_mode === "autopay" : newPaymentFlowState?.billingMode === "autopay";
        if (isAutoPay) {
          openPaymentTab(shortUrl, invoiceId, null, true);
          return;
        }
        toast.success("Redirecting to payment page...");
        window.location.href = shortUrl;
      }
    } catch (err) {
      console.error('Failed to create plan API:', err);
      // toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
      setInvoiceModalData(prev => ({ ...prev, isInvoiceDetailsModal: false }));
    }
  };

  const handlePreviewPlan = async (
    e,
    packageId,
    packageType,
    setTraceBtnClick,
    isContact = false,
    billingCycle
  ) => {
    e.preventDefault();
    if (isContact) {
      navigate('/contact');
      return;
    }
    // Early return for unauthenticated user
    if (!isLogedIn) {
      navigate('/signin');
      return;
    }

    const isPlan = packageType === "plan" || packageType == 'paymentMethod';

    // Start loading states
    setInvoiceModalData((prev) => ({ ...prev, isLoading: true }));
    setPaymentStatus(null)
    if (isPlan && setTraceBtnClick) {
      setTraceBtnClick({ isLoading: true, btnClickPackageId: packageId });
    }

    try {
      const payload = {
        package_id: packageId,
        invoice_preview_for: packageType === "upgrade" ? "upgrade_plan" : "plan",
        interval: billingCycle
      };

      setPackageId(packageId);

      const { data } = await previewPlanSummary(payload);

      setInvoiceModalData((prev) => ({
        ...prev,
        isInvoiceDetailsModal: true,
        invoiceDetails: data?.data,
        planType: packageType,
        interval: billingCycle
      }));

      // packageType=='paymentMethod' ? 'Continue' : 'Start Free Trial'
    } catch (err) {
      console.error("Failed to call create plan API:", err);
    } finally {
      // Reset loading states
      setInvoiceModalData((prev) => ({ ...prev, isLoading: false }));
      if (isPlan && setTraceBtnClick) {
        setTraceBtnClick({ isLoading: false, btnClickPackageId: null });
      }
    }
  };

  const onClosePackageSummaryModal = () => {
    setInvoiceModalData((prev) => ({
      ...prev,
      isInvoiceDetailsModal: false
    }))
  }

  const handleFailed = (failedNavigationUrl) => {
    setRazorpayModalData((prev) => ({
      ...prev,
      shortUrl: null,
      invoiceId: null,
      isLoading: false,
      isRazorPayModal: false,
      isBlockedRedirection: false,
      isPaymentExpired: false
    }))
    setInvoiceModalData((prev) => ({
      ...prev,
      isInvoiceDetailsModal: false,
      invoiceDetails: null,
      isLoading: false
    }))

    if (failedNavigationUrl) {
      window.dispatchEvent(new Event("refresh-page"));
    }
    if (currentPath === '/pricing') {
      window.dispatchEvent(new Event("refresh-page"));
    }
    else {
      navigate('/pricing')
    }
  }

  const continueToPayment = (e, currentPlan) => {
    e.preventDefault();
    const shortUrl = currentPlan?.subscription?.plan_changes?.payment_link;

    if (!shortUrl)
      toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");

    const isAutoPay = currentPlan?.subscription?.plan_changes?.billing_mode === "autopay"
    if (isAutoPay) {
      openPaymentTab(shortUrl, null, null, true,true);
      return;
    }
    toast.success("Redirecting to payment page...");
    window.location.href = shortUrl;
  }

  const handleAddOnInvoicePreview = async (setLoading, marketplaceAccountId) => {
    try {
      setLoading && setLoading(true)
      const res = await addonInvoicePreview(marketplaceAccountId)
      setAddonPreviewData((prev) => ({
        ...prev,
        isOpenModal: true,
        previewData: res?.data?.invoice,
        marketplaceAccountId
      }))
    }
    catch (err) {
      console.error('Failed to called addon invoice preview : ', err);
      toast.error(err?.message || err?.data?.message || 'Invoice preview failed', {
        position: "bottom-right"
      })
    }
    finally {
      setLoading && setLoading(false)
    }
  }

  const handleAddOnSellerAccount = async (e, marketplaceAccounts, submitArg) => {
    try {
      e.preventDefault();
      setAddonPreviewData((prev) => ({
        ...prev,
        isLoading: true,
      }))
      const marketplaceAccountIds = Array.isArray(marketplaceAccounts)
        ? marketplaceAccounts.map((account) => account?.id ?? account)  // if it's already an integer, use it directly
        : marketplaceAccounts;

      const apiRes = await addonSellerAccount(marketplaceAccountIds)
      setAddonPreviewData((prev) => ({
        ...prev,
        isOpenModal: false,
        previewData: null,
      }))
      const isPaid = apiRes?.data?.data?.paid_plan;
      const isPaymentLink = apiRes?.data?.payment_link;
      if (isPaymentLink) {
        window.history.pushState(null, "", window.location.href);
        window.onpopstate = function () {
          window.history.pushState(null, "", window.location.href);
        };
        window.open(isPaymentLink, "_self");
      }
      else if (isPaid) {
        const shortUrl = apiRes?.data?.data?.short_url;
        if (!shortUrl) {
          toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
          return;
        }
        openPaymentTab(shortUrl, apiRes?.data?.data?.invoice?.id);
      }
      else {
        toast.success(apiRes?.message || apiRes?.data?.message || 'Your subscription plan request received successfully waiting for payment confirmation and then upgrade your plan', {
          position: "bottom-right"
        })
        if (submitArg?.type === "amazonAds") {
          submitArg.action(true);
          navigate('/marketplace-accounts');
        }
        else if (submitArg) {
          submitArg.action(true);
        }
        else {
          navigate('/marketplace-accounts');
        }
      }
    }
    catch (err) {
      console.error('Failed to called addon invoice preview : ', err);
    }
    finally {
      setAddonPreviewData((prev) => ({
        ...prev,
        isLoading: false
      }))
    }
  }

  const onCloseAddOnInvoicePreview = (e, navigationUrl = null) => {
    e.preventDefault();
    setAddonPreviewData((prev) => ({
      ...prev,
      isOpenModal: false,
      previewData: null,
      isLoading: false
    }))
    if (navigationUrl) {
      navigate(navigationUrl)
    }
  }

  return (
    <RazorpayContext.Provider
      value={{
        razorpayModalData,
        setRazorpayModalData,
        invoiceModalData,
        setInvoiceModalData,
        handleCreatePlan,
        handlePreviewPlan,
        onClosePackageSummaryModal,
        handleFailed,
        addonInvoicePreviewData,
        handleAddOnInvoicePreview,
        handleAddOnSellerAccount,
        onCloseAddOnInvoicePreview,
        isRazorpayModalTimerLoader,
        setIsRazorpayModalTimerLoader,
        isPaymentStatus,
        setPaymentStatus,
        handleAddFunds,
        handleTopupPoints,
        continueToPayment
      }}
    >
      {children}
    </RazorpayContext.Provider>
  );
};

export const useRazorpay = () => useContext(RazorpayContext);